import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lock, Mail, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (ready && user) {
    const home =
      user.role === "customer" ? "/portal" :
      user.role === "driver" ? "/driver" : "/admin";
    return <Navigate to={home} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      const home =
        u.role === "customer" ? "/portal" :
        u.role === "driver" ? "/driver" : "/admin";
      navigate(home);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const heroUrl = "https://static.prod-images.emergentagent.com/jobs/07c68335-31cf-4562-8b9f-72628c4d1a81/images/ae1917618569ae7cff65de3f294d26118eacd0212babfd4a1c8af79dedbd94e8.png";

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bg)", color: "var(--text)" }}>
      {/* LEFT: Form (white) */}
      <div className="flex flex-col px-8 md:px-16 py-12">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center rounded-sm"
            style={{ background: "var(--header)", padding: "8px 10px" }}
          >
            <img src="/branding/nivara_gold.png" alt="NIVARA" className="h-7 w-auto" data-testid="login-logo" />
          </div>
          <div className="border-l pl-3" style={{ borderColor: "var(--border)" }}>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-3)" }}>Transport Portal</div>
            <div className="text-xs" style={{ color: "var(--text-2)" }}>Swiss Logistics</div>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md fade-in">
            <div className="label-eyebrow mb-3" style={{ color: "var(--gold)" }}>Anmeldung</div>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              Disponieren mit<br /><span style={{ color: "var(--header)" }}>Schweizer Präzision.</span>
            </h1>
            <p className="mt-4 text-sm leading-relaxed max-w-sm" style={{ color: "var(--text-2)" }}>
              Aufträge erfassen, Paletten etikettieren, Touren steuern — in einem Portal.
            </p>

            <form onSubmit={handleSubmit} className="mt-10 space-y-4 stagger">
              <div>
                <label className="label-eyebrow block mb-2">E-Mail</label>
                <div className="relative">
                  <Mail size={15} strokeWidth={1.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-3)" }} />
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-base !pl-11"
                    placeholder="name@nivaragroup.ch"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Passwort</label>
                <div className="relative">
                  <Lock size={15} strokeWidth={1.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-3)" }} />
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-base !pl-11"
                    placeholder="••••••••••"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {error && (
                <div data-testid="login-error" className="border text-sm px-3 py-2 rounded-sm" style={{ borderColor: "rgba(220,38,38,0.35)", background: "rgba(220,38,38,0.06)", color: "#B91C1C" }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-button"
                className="btn-gold w-full py-3 mt-2 rounded-sm flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
              >
                {submitting ? "Anmeldung…" : (<>Anmelden <ArrowRight size={16} strokeWidth={1.8} /></>)}
              </button>
            </form>
          </div>
        </div>

        <div className="text-xs mt-8 tracking-wide" style={{ color: "var(--text-3)" }}>
          © {new Date().getFullYear()} NIVARA GROUP · portal.nivaragroup.ch
        </div>
      </div>

      {/* RIGHT: Hero image with Tannengrün gradient */}
      <div className="hidden lg:block relative overflow-hidden" style={{ borderLeft: "1px solid var(--border)" }}>
        <img
          src={heroUrl}
          alt="Swiss Mountain Logistics"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e)=>{e.currentTarget.style.display='none';}}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(31,58,46,0.15) 0%, rgba(31,58,46,0.55) 60%, rgba(20,39,31,0.92) 100%)" }}
        />
        <div className="absolute bottom-0 inset-x-0 p-12 text-white">
          <div className="label-eyebrow mb-3" style={{ color: "var(--gold)" }}>Manifest</div>
          <div className="font-medium text-2xl max-w-md leading-snug">
            „Eine Lieferung ist kein Versand —
            <span className="text-white/65"> sie ist ein Versprechen.“</span>
          </div>
        </div>
      </div>
    </div>
  );
}
